import React, { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DAMAGE_TYPES, ASSET_ICONS, ASSET_LABELS } from '../utils';

export default function DamageModal({ asset, onClose, onSuccess }) {
  const { withAuth } = useAuth();
  // No default: pre-selecting a type meant a hurried report could file "screen
  // damage" without anyone actually choosing it. The reporter has to pick.
  const [damageType, setDamageType] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  async function handleSubmit() {
    if (!damageType) { setError('Please select a damage type'); return; }
    if (!description.trim()) { setError('Please describe the damage'); return; }
    setSubmitting(true);
    setError('');
    try {
      const result = await withAuth(() => api.createDamageReport({
        assetId: asset.id,
        damageType,
        description,
      }));
      setDone(result);
    } catch (err) {
      setError(err.message || 'Failed to file report');
    } finally {
      setSubmitting(false);
    }
  }

  // No click-outside-to-close: a stray backdrop click would discard typed work.
  return (
    <div className="modal-overlay">
      <div className="modal">
        {done ? (
          // Success state
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <h3 style={{ marginBottom: 8 }}>Report Filed</h3>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>
              Damage report submitted successfully.
              {done.halo_ticket_id && (
                <> HALO ticket <strong>#{done.halo_ticket_id}</strong> created automatically.</>
              )}
              {done.halo_error && (
                <><br /><span style={{ color: 'var(--yellow)' }}>⚠ {done.halo_error}</span></>
              )}
            </p>
            <button className="btn btn-primary" onClick={onSuccess}>Done</button>
          </div>
        ) : (
          <>
            <h3>Report Damage / Loss</h3>

            <div style={{
              background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
              padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 22 }}>{ASSET_ICONS[asset.asset_type]}</span>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{asset.display_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {asset.asset_tag} · {ASSET_LABELS[asset.asset_type]}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Damage Type *</label>
              <select value={damageType} onChange={e => { setDamageType(e.target.value); setError(''); }}>
                <option value="" disabled>Select a damage type…</option>
                {DAMAGE_TYPES.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what happened to the device…"
                style={{ minHeight: 100 }}
              />
            </div>

            <div style={{
              background: 'var(--accent-dim)', border: '1px solid var(--accent)',
              borderRadius: 'var(--radius-sm)', padding: '10px 14px',
              fontSize: 13, color: 'var(--text-1)', marginBottom: 16,
            }}>
              📋 A HALO ITSM ticket will be created automatically when this report is submitted.
            </div>

            {error && <div style={{ color: 'var(--red)', fontSize: 14, marginBottom: 12 }}>{error}</div>}

            <div className="modal-actions">
              <button className="btn" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
